import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchConversation,
  fetchDealerProfile,
  fetchDealerStockAlertCaps,
  fetchOutbidEngagements,
  fetchTb360BidEngagements,
  postBotPreference,
  postDealerStockAlertCapOverride,
  postPersonalisedMessagesBlocked,
  postStockFilters,
  postTb360Bidding,
  STOCK_TYPE_LABELS,
  STOCK_TYPES,
  type BotPreferenceKey,
  type BotPreferences,
  type ConversationDetail,
  type DealerProfile,
  type DealerStockAlertCaps,
  type GeneralSignalEntry,
  type OutbidEngagementEntry,
  type PaginatedOutbidEngagements,
  type PaginatedTb360BidEngagements,
  type RecommendationFeedbackEntry,
  type StockFilterRange,
  type StockFilters,
  type StockFiltersUpdate,
  type StockInsightEntry,
  type StockType,
  type StockTypeOption,
  type Tb360BidEngagementEntry,
} from "../lib/api";
import { relativeFromNow } from "../lib/format";
import { useApi } from "../hooks/use-api";
import { useDocumentTitle } from "../hooks/use-document-title";
import { OutbidEngagementDrawer, OutbidEngagementTable } from "./OutbidEngagementPage";
import { Tb360BidEngagementDrawer, Tb360BidEngagementTable } from "./Tb360BidEngagementPage";

type TabKey = "overview" | "preferences" | "feedback" | "outbid" | "tb360Bid" | "conversation";

const CONVERSATION_PREVIEW_LIMIT = 10;

const NOTIFICATION_PREFERENCE_LABELS: Record<string, string> = {
  outbidAlerts: "Outbid alerts",
  recommendations: "Stock recommendations",
  quietHoursStart: "Quiet hours start",
  quietHoursEnd: "Quiet hours end",
  quietHoursTimezone: "Quiet hours timezone",
};

const VEHICLE_PREFERENCE_LABELS: Record<string, string> = {
  excludedBrands: "Excluded brands",
  excludedBodyTypes: "Excluded body types",
  excludedFuelTypes: "Excluded fuel types",
  excludedVehicleTypes: "Excluded vehicle types",
  excludedAuctionTypes: "Excluded auction types",
  maxPrice: "Max price",
  minPrice: "Min price",
  maxMileage: "Max mileage",
  maxYear: "Max year",
  minYear: "Min year",
  preferredRegions: "Preferred regions",
  excludeDamaged: "Exclude damaged",
};

const INSIGHT_CATEGORY_LABELS: Record<string, string> = {
  price: "Price",
  mileage: "Mileage",
  year: "Year",
  make: "Make",
  model: "Model",
  body_type: "Body type",
  fuel_type: "Fuel type",
  transmission: "Transmission",
  condition: "Condition",
  location: "Location",
  other: "Other",
};

const SIGNAL_CATEGORY_LABELS: Record<string, string> = {
  overstocked: "Overstocked",
  out_of_office: "Out of office",
  budget_constrained: "Budget constrained",
  industry_event: "Industry event",
  other: "Other",
};

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "None";
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(text: string, max = 120): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function vehicleSummary(v: RecommendationFeedbackEntry["vehicleContext"]): string {
  const parts: string[] = [];
  if (v.year) parts.push(String(v.year));
  if (v.brand) parts.push(v.brand);
  if (v.model) parts.push(v.model);
  if (parts.length === 0 && v.vehicleTitle) return v.vehicleTitle;
  return parts.join(" ") || `Project ${v.projectId}`;
}

function DealerHeader({ profile }: { profile: DealerProfile }) {
  const { dealer, botPreferences, feedback, insights, preferences } = profile;
  const insightCount = insights?.insights.length ?? 0;
  const signalCount = insights?.generalSignals.length ?? 0;
  const feedbackCount = feedback.length;

  return (
    <div className="card dealer-profile-header">
      <div className="dealer-profile-header-main">
        <div className="dealer-profile-identity">
          <div className="dealer-profile-name">{dealer.displayName}</div>
          <div className="dealer-profile-org">{dealer.orgName}</div>
          <div className="dealer-profile-meta">
            <span className="mono">{dealer.phone}</span>
            {dealer.email && <span className="mono"> · {dealer.email}</span>}
            <span> · ID #{dealer.dealerId}</span>
            <span> · {dealer.channel === "whatsapp" ? "WhatsApp" : dealer.channel}</span>
          </div>
        </div>
        <div className="dealer-profile-pills">
          <span className={`badge ${dealer.status === "Active" ? "badge-success" : "badge-warning"}`}>
            {dealer.status === "Active" ? "Active" : `Inactive (${dealer.status})`}
          </span>
          <span className={`badge ${botPreferences.ottoBot ? "badge-success" : "badge-neutral"}`}>
            Otto bot {botPreferences.ottoBot ? "on" : "off"}
          </span>
          <span className={`badge ${botPreferences.outbidBot ? "badge-success" : "badge-neutral"}`}>
            Outbid alerts {botPreferences.outbidBot ? "on" : "off"}
          </span>
          <span className={`badge ${botPreferences.stockNotifications ? "badge-success" : "badge-neutral"}`}>
            Stock alerts {botPreferences.stockNotifications ? "on" : "off"}
          </span>
        </div>
      </div>
      <div className="dealer-profile-stats stat-grid">
        <div className="stat-card">
          <div className="stat-value">{feedbackCount}</div>
          <div className="stat-label">'Not for me' (recent)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{insightCount}</div>
          <div className="stat-label">Stock insights</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{signalCount}</div>
          <div className="stat-label">General signals</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">v{preferences.version}</div>
          <div className="stat-label">Preferences version</div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({
  profile,
  outbid,
}: {
  profile: DealerProfile;
  outbid: PaginatedOutbidEngagements | null;
}) {
  const { dealer, feedback, insights } = profile;
  const lastOutbidAttempt = outbid?.items[0]?.respondedAt;
  return (
    <div className="dealer-profile-grid">
      <div className="card">
        <div className="section-title">Identity</div>
        <ul className="pref-list">
          <li><span>Name</span><span>{dealer.displayName}</span></li>
          <li><span>Organisation</span><span>{dealer.orgName}</span></li>
          <li><span>Phone</span><span className="mono">{dealer.phone}</span></li>
          {dealer.email && <li><span>Email</span><span className="mono">{dealer.email}</span></li>}
          <li><span>Channel</span><span>{dealer.channel === "whatsapp" ? "WhatsApp" : dealer.channel}</span></li>
          <li><span>Dealer ID</span><span className="mono">#{dealer.dealerId}</span></li>
        </ul>
      </div>
      <div className="card">
        <div className="section-title">Activity snapshot</div>
        <ul className="pref-list">
          <li>
            <span>Recent 'Not for me'</span>
            <span>{feedback.length}</span>
          </li>
          <li>
            <span>Stock insights captured</span>
            <span>{insights?.insights.length ?? 0}</span>
          </li>
          <li>
            <span>Recent signals</span>
            <span>{insights?.generalSignals.length ?? 0}</span>
          </li>
          {insights && (
            <li>
              <span>Insights last updated</span>
              <span>{relativeFromNow(insights.updatedAt)}</span>
            </li>
          )}
          <li>
            <span>Outbid engagements</span>
            <span>{outbid ? outbid.total : "—"}</span>
          </li>
          <li>
            <span>Last outbid attempt</span>
            <span title={lastOutbidAttempt ? formatTimestamp(lastOutbidAttempt) : undefined}>
              {lastOutbidAttempt ? relativeFromNow(lastOutbidAttempt) : "—"}
            </span>
          </li>
        </ul>
      </div>
      <div className="card dealer-profile-quick-links">
        <div className="section-title">Quick links</div>
        <div className="dealer-profile-links">
          <Link
            to={`/observability?dealerId=${dealer.dealerId}`}
            className="btn btn-sm"
          >
            View observability events
          </Link>
        </div>
      </div>
    </div>
  );
}

function PreferencesTab({
  profile,
  dealerId,
  onStockFiltersSaved,
}: {
  profile: DealerProfile;
  dealerId: string;
  onStockFiltersSaved: (filters: StockFilters) => void;
}) {
  const { preferences } = profile;
  return (
    <div className="dealer-profile-grid">
      <div className="card">
        <div className="section-title">Notification preferences</div>
        <ul className="pref-list">
          {Object.entries(preferences.notifications).map(([key, value]) => (
            <li key={key}>
              <span>{NOTIFICATION_PREFERENCE_LABELS[key] ?? key}</span>
              <span className="mono">{renderValue(value)}</span>
            </li>
          ))}
        </ul>
      </div>
      <StockFiltersCard
        dealerId={dealerId}
        initialFilters={profile.stockFilters ?? EMPTY_STOCK_FILTERS}
        stockTypeOptions={profile.stockTypeOptions ?? []}
        onSaved={onStockFiltersSaved}
      />
      <StockAlertCapsCard dealerId={dealerId} />
    </div>
  );
}

type RangeKey = "age" | "mileage" | "price";
type ListKey = "makes" | "bodyTypes";

const EMPTY_STOCK_FILTERS: StockFilters = { stock_type: [] };

interface StockFiltersDraft {
  stockTypes: string[];
  ageMin: string;
  ageMax: string;
  mileageMin: string;
  mileageMax: string;
  priceMin: string;
  priceMax: string;
  makes: string;
  bodyTypes: string;
}

const RANGE_FIELDS: Array<{ key: RangeKey; label: string; unit: string; hint: string }> = [
  { key: "age", label: "Vehicle age", unit: "years", hint: "Age in years, based on the vehicle's registration year." },
  { key: "mileage", label: "Mileage", unit: "km", hint: "Odometer reading in km." },
  { key: "price", label: "Price", unit: "start price", hint: "Listing start price. Listings with no start price are never filtered out." },
];

const LIST_FIELDS: Array<{ key: ListKey; label: string; placeholder: string; hint: string }> = [
  {
    key: "makes",
    label: "Makes (allow-list)",
    placeholder: "e.g. Audi, BMW, Ford",
    hint: "Only listings matching one of these makes will be sent to this dealer. Leave empty to allow all makes.",
  },
  {
    key: "bodyTypes",
    label: "Body types (allow-list)",
    placeholder: "e.g. Hatchback, SUV",
    hint: "Only listings matching one of these body types will be sent to this dealer. Leave empty to allow all body types.",
  },
];

function draftFromFilters(filters: StockFilters): StockFiltersDraft {
  const bound = (value: number | undefined) => (value === undefined ? "" : String(value));
  return {
    stockTypes: filters.stock_type ?? [],
    ageMin: bound(filters.age?.min),
    ageMax: bound(filters.age?.max),
    mileageMin: bound(filters.mileage?.min),
    mileageMax: bound(filters.mileage?.max),
    priceMin: bound(filters.price?.min),
    priceMax: bound(filters.price?.max),
    makes: (filters.makes ?? []).join(", "),
    bodyTypes: (filters.bodyTypes ?? []).join(", "),
  };
}

function parseDraftRange(label: string, minRaw: string, maxRaw: string): StockFilterRange | null | string {
  const range: StockFilterRange = {};
  for (const [side, raw] of [["min", minRaw], ["max", maxRaw]] as const) {
    if (raw.trim() === "") continue;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return `${label}: enter a number of 0 or more`;
    range[side] = value;
  }
  if (range.min !== undefined && range.max !== undefined && range.min > range.max) {
    return `${label}: minimum can't be greater than maximum`;
  }
  return range.min === undefined && range.max === undefined ? null : range;
}

function parseDraftList(raw: string): string[] | null {
  const list = raw.split(",").map((item) => item.trim()).filter(Boolean);
  return list.length > 0 ? list : null;
}

/** Builds the full update (every field, null = no restriction) or an error message. */
function updateFromDraft(draft: StockFiltersDraft): StockFiltersUpdate | string {
  const update: StockFiltersUpdate = { stock_type: draft.stockTypes };
  for (const field of RANGE_FIELDS) {
    const parsed = parseDraftRange(field.label, draft[`${field.key}Min`], draft[`${field.key}Max`]);
    if (typeof parsed === "string") return parsed;
    update[field.key] = parsed;
  }
  for (const field of LIST_FIELDS) {
    update[field.key] = parseDraftList(draft[field.key]);
  }
  return update;
}

function filtersFromUpdate(update: StockFiltersUpdate): StockFilters {
  return {
    stock_type: update.stock_type ?? [],
    age: update.age ?? undefined,
    mileage: update.mileage ?? undefined,
    price: update.price ?? undefined,
    makes: update.makes ?? undefined,
    bodyTypes: update.bodyTypes ?? undefined,
  };
}

function summariseRange(range: StockFilterRange | undefined, unit: string): string {
  if (!range) return "Any";
  if (range.min !== undefined && range.max !== undefined) {
    return `${range.min.toLocaleString()}–${range.max.toLocaleString()} ${unit}`;
  }
  if (range.min !== undefined) return `${range.min.toLocaleString()}+ ${unit}`;
  if (range.max !== undefined) return `Up to ${range.max.toLocaleString()} ${unit}`;
  return "Any";
}

/**
 * Editor for the seller_config.stock_filters checked on every stock alert by
 * passesPersonalisedFilters. Internal-team only: the dealer can't set any of
 * these over WhatsApp (stock types are dealer-managed and not edited here).
 */
function StockFiltersCard({
  dealerId,
  initialFilters,
  stockTypeOptions,
  onSaved,
}: {
  dealerId: string;
  initialFilters: StockFilters;
  stockTypeOptions: StockTypeOption[];
  onSaved: (filters: StockFilters) => void;
}) {
  const [saved, setSaved] = useState<StockFilters>(initialFilters);
  const [draft, setDraft] = useState<StockFiltersDraft>(() => draftFromFilters(initialFilters));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSaved(initialFilters);
    setDraft(draftFromFilters(initialFilters));
  }, [initialFilters]);

  function updateDraft(key: Exclude<keyof StockFiltersDraft, "stockTypes">, value: string) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function toggleStockType(value: string, enabled: boolean) {
    setDraft((current) => ({
      ...current,
      stockTypes: enabled
        ? [...current.stockTypes.filter((type) => type !== value), value]
        : current.stockTypes.filter((type) => type !== value),
    }));
  }

  const labelForStockType = (value: string) =>
    stockTypeOptions.find((option) => option.value === value)?.label ?? value;

  async function save() {
    const update = updateFromDraft(draft);
    if (typeof update === "string") {
      setError(update);
      return;
    }
    const previous = saved;
    setSaving(true);
    setError(null);
    setSaved(filtersFromUpdate(update));
    try {
      const result = await postStockFilters(dealerId, update);
      setSaved(result.stockFilters);
      setDraft(draftFromFilters(result.stockFilters));
      onSaved(result.stockFilters);
    } catch (err: unknown) {
      setSaved(previous);
      setError(err instanceof Error ? err.message : "Failed to update stock filters");
    } finally {
      setSaving(false);
    }
  }

  const unitFor = (key: RangeKey) => RANGE_FIELDS.find((field) => field.key === key)?.unit ?? "";

  return (
    <div className="card">
      <div className="card-header">
        <div className="section-title" style={{ margin: 0 }}>
          Stock alert filters <span className="badge badge-neutral">TradeBid team only</span>
        </div>
        {saving && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Saving…</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8 }}>
        Stock filters applied to every stock alert this dealer is sent. A listing must pass all of them. Leave a
        range or list empty for no limit.
      </div>
      {error && <div className="error-msg">{error}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, opacity: saving ? 0.7 : 1, fontSize: 13 }}>
        <div>
          <div style={{ fontWeight: 600, color: "var(--color-text)" }}>Stock types</div>
          <div style={{ display: "flex", gap: 16, marginTop: 4, flexWrap: "wrap" }}>
            {stockTypeOptions.map((option) => (
              <label key={option.value} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={draft.stockTypes.includes(option.value)}
                  disabled={saving}
                  onChange={(e) => toggleStockType(option.value, e.target.checked)}
                />
                {option.label}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
            Stock alerts are only sent for the types ticked here (and only while Stock alerts is on in Bot
            preferences).
          </div>
          {draft.stockTypes.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--color-error)", marginTop: 2 }}>
              No types selected = this dealer receives no stock alerts at all.
            </div>
          )}
        </div>
        {RANGE_FIELDS.map((field) => (
          <div key={field.key}>
            <div style={{ fontWeight: 600, color: "var(--color-text)" }}>{field.label}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
              <input
                type="number"
                min={0}
                placeholder="Min"
                aria-label={`${field.label} minimum`}
                value={draft[`${field.key}Min`]}
                disabled={saving}
                onChange={(e) => updateDraft(`${field.key}Min`, e.target.value)}
                style={{ width: 110 }}
              />
              <span>to</span>
              <input
                type="number"
                min={0}
                placeholder="Max"
                aria-label={`${field.label} maximum`}
                value={draft[`${field.key}Max`]}
                disabled={saving}
                onChange={(e) => updateDraft(`${field.key}Max`, e.target.value)}
                style={{ width: 110 }}
              />
              <span style={{ color: "var(--color-text-secondary)" }}>{field.unit}</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>{field.hint}</div>
          </div>
        ))}
        {LIST_FIELDS.map((field) => (
          <div key={field.key}>
            <div style={{ fontWeight: 600, color: "var(--color-text)" }}>{field.label}</div>
            <input
              type="text"
              placeholder={field.placeholder}
              aria-label={field.label}
              value={draft[field.key]}
              disabled={saving}
              onChange={(e) => updateDraft(field.key, e.target.value)}
              style={{ width: "100%", marginTop: 4 }}
            />
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {field.hint} Separate entries with commas; matching ignores case.
            </div>
          </div>
        ))}
        <div>
          <button type="button" className="btn btn-sm" disabled={saving} onClick={() => void save()}>
            Save stock filters
          </button>
        </div>
      </div>
      <div className="section-title" style={{ marginTop: 16 }}>Currently applied</div>
      <ul className="pref-list">
        <li>
          <span>Stock types</span>
          <span className="mono">
            {saved.stock_type.length ? saved.stock_type.map(labelForStockType).join(", ") : "None (no stock alerts)"}
          </span>
        </li>
        {RANGE_FIELDS.map((field) => (
          <li key={field.key}>
            <span>{field.label}</span>
            <span className="mono">{summariseRange(saved[field.key], unitFor(field.key))}</span>
          </li>
        ))}
        <li>
          <span>Makes</span>
          <span className="mono">{saved.makes?.length ? `Only ${saved.makes.join(", ")}` : "All makes"}</span>
        </li>
        <li>
          <span>Body types</span>
          <span className="mono">
            {saved.bodyTypes?.length ? `Only ${saved.bodyTypes.join(", ")}` : "All body types"}
          </span>
        </li>
      </ul>
    </div>
  );
}

/**
 * Read-only view of the DealerPreferences.vehicles object the WhatsApp
 * conversational flow builds from what the dealer has told the bot. Context
 * only: it is not what controls stock-alert delivery (see StockFiltersCard).
 */
function ConversationalVehiclePreferencesCard({ vehicles }: { vehicles: Record<string, unknown> }) {
  return (
    <div className="card">
      <div className="section-title">Vehicle preferences</div>
      <ul className="pref-list">
        {Object.entries(vehicles).map(([key, value]) => (
          <li key={key}>
            <span>{VEHICLE_PREFERENCE_LABELS[key] ?? key}</span>
            <span className="mono">{renderValue(value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type CapOverrideValue = number | null | "inherit";
type CapMode = "default" | "custom" | "unlimited";

function formatCap(cap: number | null): string {
  return cap === null ? "Unlimited" : `${cap} / day`;
}

function capModeFor(caps: DealerStockAlertCaps, stockType: StockType): CapMode {
  if (!(stockType in caps.overrides)) return "default";
  return caps.overrides[stockType] === null ? "unlimited" : "custom";
}

function StockAlertCapRow({
  stockType,
  caps,
  isPending,
  onApply,
}: {
  stockType: StockType;
  caps: DealerStockAlertCaps;
  isPending: boolean;
  onApply: (value: CapOverrideValue) => void;
}) {
  const savedMode = capModeFor(caps, stockType);
  const [mode, setMode] = useState<CapMode>(savedMode);
  const [customValue, setCustomValue] = useState(() => {
    const override = caps.overrides[stockType];
    return typeof override === "number" ? String(override) : "";
  });
  const [inputError, setInputError] = useState<string | null>(null);

  useEffect(() => {
    setMode(savedMode);
  }, [savedMode]);

  const name = `stock-alert-cap-${stockType}`;

  function selectMode(next: CapMode) {
    setMode(next);
    setInputError(null);
    if (next === "default") onApply("inherit");
    if (next === "unlimited") onApply(null);
  }

  function applyCustom() {
    const parsed = Number(customValue);
    if (customValue.trim() === "" || !Number.isInteger(parsed) || parsed < 0) {
      setInputError("Enter a whole number of 0 or more");
      return;
    }
    setInputError(null);
    onApply(parsed);
  }

  return (
    <div
      style={{
        padding: "12px 14px",
        background: "var(--color-surface-solid)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-sm)",
        opacity: isPending ? 0.7 : 1,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontWeight: 600, color: "var(--color-text)" }}>{STOCK_TYPE_LABELS[stockType]}</div>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          <span className="mono">{formatCap(caps.effective[stockType])}</span>{" "}
          {savedMode === "default" ? "(platform default)" : "(dealer override)"}
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 8, flexWrap: "wrap", fontSize: 13 }}>
        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="radio"
            name={name}
            checked={mode === "default"}
            disabled={isPending}
            onChange={() => selectMode("default")}
          />
          Default ({formatCap(caps.platformDefaults[stockType])})
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="radio"
            name={name}
            checked={mode === "custom"}
            disabled={isPending}
            onChange={() => selectMode("custom")}
          />
          Custom
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="radio"
            name={name}
            checked={mode === "unlimited"}
            disabled={isPending}
            onChange={() => selectMode("unlimited")}
          />
          Unlimited
        </label>
      </div>
      {mode === "custom" && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
          <input
            type="number"
            min={0}
            step={1}
            value={customValue}
            disabled={isPending}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyCustom();
            }}
            style={{ width: 100 }}
          />
          <button type="button" className="btn btn-sm" disabled={isPending} onClick={applyCustom}>
            Set
          </button>
          {inputError && <span style={{ fontSize: 12, color: "var(--color-error)" }}>{inputError}</span>}
        </div>
      )}
    </div>
  );
}

function StockAlertCapsCard({ dealerId }: { dealerId: string }) {
  const { data, loading, error: loadError } = useApi(() => fetchDealerStockAlertCaps(dealerId), [dealerId]);
  const [caps, setCaps] = useState<DealerStockAlertCaps | null>(null);
  const [pendingType, setPendingType] = useState<StockType | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setCaps(data);
  }, [data]);

  async function applyOverride(stockType: StockType, value: CapOverrideValue) {
    if (!caps) return;
    setPendingType(stockType);
    setError(null);
    const previous = caps;
    const overrides = { ...caps.overrides };
    if (value === "inherit") {
      delete overrides[stockType];
    } else {
      overrides[stockType] = value;
    }
    setCaps({
      ...caps,
      overrides,
      effective: { ...caps.effective, [stockType]: value === "inherit" ? caps.platformDefaults[stockType] : value },
    });
    try {
      setCaps(await postDealerStockAlertCapOverride(dealerId, stockType, value));
    } catch (err: unknown) {
      setCaps(previous);
      setError(err instanceof Error ? err.message : "Failed to update stock alert cap");
    } finally {
      setPendingType(null);
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="section-title" style={{ margin: 0 }}>Stock alert caps</div>
        {pendingType && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Saving…</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8 }}>
        Maximum stock alerts this dealer receives per day, per stock type.
      </div>
      {loading && <div className="loading">Loading caps...</div>}
      {loadError && <div className="error-msg">{loadError}</div>}
      {error && <div className="error-msg">{error}</div>}
      {caps && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {STOCK_TYPES.map((stockType) => (
            <StockAlertCapRow
              key={stockType}
              stockType={stockType}
              caps={caps}
              isPending={pendingType === stockType}
              onApply={(value) => void applyOverride(stockType, value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InsightsCard({ insights }: { insights: StockInsightEntry[] }) {
  if (insights.length === 0) {
    return (
      <div className="card">
        <div className="section-title">Stock preferences (LLM-derived)</div>
        <div className="empty-state">
          No insights yet. They'll start appearing here once dealers respond to 'Not for me'
          prompts and the analysis pipeline picks up the reasons.
        </div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="section-title">Stock preferences (LLM-derived)</div>
      <table>
        <thead>
          <tr>
            <th style={{ width: "120px" }}>Category</th>
            <th>Insight</th>
            <th style={{ width: "60px", textAlign: "right" }}>Seen</th>
            <th style={{ width: "100px" }}>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {insights.map((insight) => (
            <tr key={insight.insightId}>
              <td>
                <span className="badge badge-info">
                  {INSIGHT_CATEGORY_LABELS[insight.category] ?? insight.category}
                </span>
              </td>
              <td>{insight.text}</td>
              <td style={{ textAlign: "right" }}>{insight.occurrenceCount}</td>
              <td className="mono" title={formatTimestamp(insight.lastSeenAt)}>
                {relativeFromNow(insight.lastSeenAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SignalsCard({ signals }: { signals: GeneralSignalEntry[] }) {
  if (signals.length === 0) {
    return null;
  }
  return (
    <div className="card">
      <div className="section-title">Recent signals (general)</div>
      <table>
        <thead>
          <tr>
            <th style={{ width: "150px" }}>Category</th>
            <th>Signal</th>
            <th style={{ width: "60px", textAlign: "right" }}>Seen</th>
            <th style={{ width: "100px" }}>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {signals.map((signal) => (
            <tr key={signal.signalId}>
              <td>
                <span className="badge badge-warning">
                  {SIGNAL_CATEGORY_LABELS[signal.category] ?? signal.category}
                </span>
              </td>
              <td>{signal.text}</td>
              <td style={{ textAlign: "right" }}>{signal.occurrenceCount}</td>
              <td className="mono" title={formatTimestamp(signal.lastSeenAt)}>
                {relativeFromNow(signal.lastSeenAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FeedbackCard({ feedback }: { feedback: RecommendationFeedbackEntry[] }) {
  if (feedback.length === 0) {
    return (
      <div className="card">
        <div className="section-title">Recent 'Not for me' feedback</div>
        <div className="empty-state">No feedback recorded yet.</div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="section-title">Recent 'Not for me' feedback ({feedback.length})</div>
      <table>
        <thead>
          <tr>
            <th style={{ width: "100px" }}>When</th>
            <th>Vehicle</th>
            <th>Reason</th>
            <th style={{ width: "100px" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {feedback.map((entry) => (
            <tr key={entry.feedbackId}>
              <td className="mono" title={formatTimestamp(entry.createdAt)}>
                {relativeFromNow(entry.createdAt)}
              </td>
              <td>
                <div>{vehicleSummary(entry.vehicleContext)}</div>
                <div className="dealer-profile-feedback-meta">
                  Project #{entry.projectId}
                  {entry.vehicleContext.estimatedPrice
                    ? ` · est. ${entry.vehicleContext.estimatedPrice.toLocaleString()}`
                    : ""}
                </div>
              </td>
              <td>
                {entry.reason ? (
                  <span title={entry.reason}>{truncate(entry.reason)}</span>
                ) : (
                  <span className="dealer-profile-feedback-pending">(no reason given yet)</span>
                )}
              </td>
              <td>
                {entry.analysedAt ? (
                  <span className="badge badge-success">analysed</span>
                ) : entry.reason ? (
                  <span className="badge badge-warning">pending analysis</span>
                ) : (
                  <span className="badge badge-neutral">awaiting reply</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FeedbackTab({ profile }: { profile: DealerProfile }) {
  const insights = profile.insights;
  return (
    <div className="dealer-profile-stack">
      <InsightsCard insights={insights?.insights ?? []} />
      <SignalsCard signals={insights?.generalSignals ?? []} />
      <FeedbackCard feedback={profile.feedback} />
      <ConversationalVehiclePreferencesCard vehicles={profile.preferences.vehicles} />
    </div>
  );
}

function OutbidEngagementTab({
  outbid,
  loading,
  error,
}: {
  outbid: PaginatedOutbidEngagements | null;
  loading: boolean;
  error: string | null;
}) {
  const [selected, setSelected] = useState<OutbidEngagementEntry | null>(null);
  const items = outbid?.items ?? [];
  return (
    <div className="dealer-profile-stack">
      <div className="card">
        <div className="section-title">
          Outbid engagements{outbid && outbid.total > 0 ? ` (${outbid.total})` : ""}
        </div>
        {loading && <div className="loading">Loading outbid engagements...</div>}
        {error && <div className="error-msg">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="empty-state">No outbid engagements yet.</div>
        )}
        {items.length > 0 && (
          <OutbidEngagementTable items={items} showDealer={false} onSelect={setSelected} />
        )}
        {outbid && outbid.total > items.length && (
          <div className="dealer-profile-feedback-meta" style={{ marginTop: 8 }}>
            Showing the latest {items.length} of {outbid.total}.
          </div>
        )}
      </div>
      {selected && <OutbidEngagementDrawer engagement={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Tb360BidEngagementTab({
  tb360Bid,
  loading,
  error,
}: {
  tb360Bid: PaginatedTb360BidEngagements | null;
  loading: boolean;
  error: string | null;
}) {
  const [selected, setSelected] = useState<Tb360BidEngagementEntry | null>(null);
  const items = tb360Bid?.items ?? [];
  return (
    <div className="dealer-profile-stack">
      <div className="card">
        <div className="section-title">
          TB360 bid engagements{tb360Bid && tb360Bid.total > 0 ? ` (${tb360Bid.total})` : ""}
        </div>
        {loading && <div className="loading">Loading TB360 bid engagements...</div>}
        {error && <div className="error-msg">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="empty-state">No TB360 bid engagements yet.</div>
        )}
        {items.length > 0 && (
          <Tb360BidEngagementTable items={items} showDealer={false} onSelect={setSelected} />
        )}
        {tb360Bid && tb360Bid.total > items.length && (
          <div className="dealer-profile-feedback-meta" style={{ marginTop: 8 }}>
            Showing the latest {items.length} of {tb360Bid.total}.
          </div>
        )}
      </div>
      {selected && <Tb360BidEngagementDrawer engagement={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ConversationPreviewTab({
  dealerId,
  conversation,
  loading,
  error,
}: {
  dealerId: string;
  conversation: ConversationDetail | null;
  loading: boolean;
  error: string | null;
}) {
  const messages = conversation?.messages ?? [];
  return (
    <div className="dealer-profile-stack">
      <div className="card">
        <div className="card-header">
          <div className="section-title">Conversation</div>
          <Link to={`/conversation/${dealerId}`} className="btn btn-sm">
            View full conversation
          </Link>
        </div>
        {loading && <div className="loading">Loading conversation...</div>}
        {error && <div className="error-msg">{error}</div>}
        {!loading && !error && messages.length === 0 && (
          <div className="empty-state">No messages in this conversation</div>
        )}
        {messages.length > 0 && (
          <div className="message-list">
            {messages.map((msg) => (
              <div
                key={msg.messageId}
                className={`message-bubble ${msg.direction}${msg.messageType === "template" ? " template" : ""}`}
              >
                <div>{msg.content}</div>
                <div className="message-meta">
                  {msg.direction === "inbound" ? "Dealer" : "Otto"} &middot;{" "}
                  <span className="mono">{new Date(msg.timestamp).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

type ToggleSpec = { key: BotPreferenceKey; label: string; description: string };

const BOT_PREFERENCE_TOGGLES: ToggleSpec[] = [
  {
    key: "otto_bot",
    label: "Otto bot",
    description:
      "Master switch for all Otto WhatsApp interactions. Turning OFF mutes inbound + outbound. Turning ON sends a welcome message.",
  },
  {
    key: "outbid_bot",
    label: "Outbid alerts",
    description: "Notify when a bid is beaten on a vehicle this dealer has bid on.",
  },
  {
    key: "stock_notifications",
    label: "Stock alerts",
    description: "Send curated new-listing alerts based on this dealer's stock filters.",
  },
];

function BotPreferenceToggle({
  spec,
  current,
  isPending,
  disabled,
  onChange,
}: {
  spec: Pick<ToggleSpec, "label" | "description">;
  current: boolean;
  isPending: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        gap: 16,
        padding: "12px 14px",
        background: "var(--color-surface-solid)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-sm)",
        cursor: disabled ? "not-allowed" : isPending ? "progress" : "pointer",
        alignItems: "flex-start",
        opacity: disabled ? 0.5 : isPending ? 0.7 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={current}
        disabled={isPending || disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: "var(--color-text)" }}>{spec.label}</div>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
          {spec.description}
        </div>
      </div>
    </label>
  );
}

function BotPreferencesCard({
  dealerId,
  prefs,
  isActive,
  onChange,
}: {
  dealerId: string;
  prefs: BotPreferences;
  isActive: boolean;
  onChange: (next: BotPreferences) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<BotPreferenceKey | null>(null);
  const [welcomeStatus, setWelcomeStatus] = useState<string | null>(null);

  async function applyToggle(key: BotPreferenceKey, value: boolean) {
    setPendingKey(key);
    setWelcomeStatus(null);
    setError(null);
    const previous = prefs;
    onChange({
      ...prefs,
      ottoBot: key === "otto_bot" ? value : prefs.ottoBot,
      outbidBot: key === "outbid_bot" ? value : prefs.outbidBot,
      stockNotifications: key === "stock_notifications" ? value : prefs.stockNotifications,
    });
    try {
      const result = await postBotPreference(dealerId, key, value);
      if (Object.keys(result.cascaded).length > 0) {
        onChange({
          ...prefs,
          ottoBot: key === "otto_bot" ? value : prefs.ottoBot,
          outbidBot: result.cascaded.outbid_bot ?? (key === "outbid_bot" ? value : prefs.outbidBot),
          stockNotifications:
            result.cascaded.stock_notifications ?? (key === "stock_notifications" ? value : prefs.stockNotifications),
        });
      }
      if (result.welcomeSent) {
        setWelcomeStatus("Welcome message sent.");
      } else {
        const cascadedLabels = [
          result.cascaded.outbid_bot !== undefined ? "Outbid" : null,
          result.cascaded.stock_notifications !== undefined ? "stock notifications" : null,
        ].filter((l): l is string => l !== null);
        if (cascadedLabels.length > 0) {
          setWelcomeStatus(`${cascadedLabels.join(" and ")} ${cascadedLabels.length > 1 ? "were" : "was"} also turned off.`);
        }
      }
    } catch (err: unknown) {
      onChange(previous);
      setError(err instanceof Error ? err.message : "Failed to update preference");
    } finally {
      setPendingKey(null);
    }
  }

  const currentMap: Record<BotPreferenceKey, boolean> = {
    otto_bot: prefs.ottoBot,
    outbid_bot: prefs.outbidBot,
    stock_notifications: prefs.stockNotifications,
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Bot preferences</h2>
        {pendingKey && (
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Saving…</span>
        )}
      </div>
      {!isActive && (
        <div className="error-msg">
          This dealer is not Active - bot preferences can't be changed, but existing settings and history remain visible.
        </div>
      )}
      {error && <div className="error-msg">{error}</div>}
      {welcomeStatus && (
        <div
          style={{
            fontSize: 12,
            color: "var(--color-accent)",
            marginBottom: 8,
          }}
        >
          {welcomeStatus}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {BOT_PREFERENCE_TOGGLES.map((spec) => (
          <BotPreferenceToggle
            key={spec.key}
            spec={spec}
            current={currentMap[spec.key]}
            isPending={pendingKey === spec.key}
            disabled={!isActive}
            onChange={(next) => void applyToggle(spec.key, next)}
          />
        ))}
      </div>
    </div>
  );
}

const TB360_BIDDING_TOGGLE = {
  label: "TradeBid 360 bidding",
  description:
    "Adds a 'Place a bid now' button to this dealer's TradeBid 360 stock alerts so they can bid over WhatsApp. Only applies to listings where they're a designated trader and they've opted into TradeBid 360 alerts.",
};

const PERSONALISED_MESSAGES_BLOCKED_TOGGLE = {
  label: "Block personalised stock messages",
  description:
    "Kill switch for dealers who've asked not to receive personalised stock messages. When on, no personalised recommendation is ever sent to this dealer, from the dashboard or any upstream tool.",
};

type InternalCapabilityKey = "tb360_bidding" | "personalised_messages_blocked";

/**
 * Capabilities only the TradeBid team can grant. Kept apart from
 * BotPreferencesCard because those mirror settings the dealer can also change
 * themselves over WhatsApp; nothing here is visible to or controllable by
 * the dealer.
 */
function InternalCapabilitiesCard({
  dealerId,
  initialTb360Bidding,
  initialPersonalisedMessagesBlocked,
  isActive,
}: {
  dealerId: string;
  initialTb360Bidding: boolean;
  initialPersonalisedMessagesBlocked: boolean;
  isActive: boolean;
}) {
  const [tb360Bidding, setTb360Bidding] = useState(initialTb360Bidding);
  const [personalisedMessagesBlocked, setPersonalisedMessagesBlocked] = useState(
    initialPersonalisedMessagesBlocked,
  );
  const [pendingKey, setPendingKey] = useState<InternalCapabilityKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTb360Bidding(initialTb360Bidding);
  }, [initialTb360Bidding]);

  useEffect(() => {
    setPersonalisedMessagesBlocked(initialPersonalisedMessagesBlocked);
  }, [initialPersonalisedMessagesBlocked]);

  async function applyTb360Toggle(next: boolean) {
    const previous = tb360Bidding;
    setPendingKey("tb360_bidding");
    setError(null);
    setTb360Bidding(next);
    try {
      const result = await postTb360Bidding(dealerId, next);
      setTb360Bidding(result.value);
    } catch (err: unknown) {
      setTb360Bidding(previous);
      setError(err instanceof Error ? err.message : "Failed to update capability");
    } finally {
      setPendingKey(null);
    }
  }

  async function applyPersonalisedMessagesBlockedToggle(next: boolean) {
    const previous = personalisedMessagesBlocked;
    setPendingKey("personalised_messages_blocked");
    setError(null);
    setPersonalisedMessagesBlocked(next);
    try {
      const result = await postPersonalisedMessagesBlocked(dealerId, next);
      setPersonalisedMessagesBlocked(result.value);
    } catch (err: unknown) {
      setPersonalisedMessagesBlocked(previous);
      setError(err instanceof Error ? err.message : "Failed to update capability");
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
          Internal capabilities <span className="badge badge-neutral">TradeBid team only</span>
        </h2>
        {pendingKey && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Saving…</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8 }}>
        Set by the TradeBid team only. The dealer can't see or change these settings.
      </div>
      {!isActive && (
        <div className="error-msg">
          This dealer is not Active - capabilities can't be changed, but existing settings remain visible.
        </div>
      )}
      {error && <div className="error-msg">{error}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <BotPreferenceToggle
          spec={TB360_BIDDING_TOGGLE}
          current={tb360Bidding}
          isPending={pendingKey === "tb360_bidding"}
          disabled={!isActive}
          onChange={(next) => void applyTb360Toggle(next)}
        />
        <BotPreferenceToggle
          spec={PERSONALISED_MESSAGES_BLOCKED_TOGGLE}
          current={personalisedMessagesBlocked}
          isPending={pendingKey === "personalised_messages_blocked"}
          disabled={!isActive}
          onChange={(next) => void applyPersonalisedMessagesBlockedToggle(next)}
        />
      </div>
    </div>
  );
}

const OUTBID_ENGAGEMENT_LIMIT = 100;

export function DealerProfilePage() {
  const { dealerId } = useParams<{ dealerId: string }>();
  const { data, loading, error } = useApi(
    () => fetchDealerProfile(dealerId!),
    [dealerId],
  );
  const outbid = useApi(
    () => fetchOutbidEngagements({ dealerId: dealerId!, limit: OUTBID_ENGAGEMENT_LIMIT }),
    [dealerId],
  );
  const tb360Bid = useApi(
    () => fetchTb360BidEngagements({ dealerId: dealerId!, limit: OUTBID_ENGAGEMENT_LIMIT }),
    [dealerId],
  );
  const conversationPreview = useApi(
    () => fetchConversation(dealerId!, { limit: CONVERSATION_PREVIEW_LIMIT }),
    [dealerId],
  );
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [liveBotPrefs, setLiveBotPrefs] = useState<BotPreferences | null>(null);
  // Kept here so saved stock filters survive switching away from the Preferences tab.
  const [liveStockFilters, setLiveStockFilters] = useState<StockFilters | null>(null);
  useDocumentTitle(
    data?.dealer.displayName
      ? `Dealer (${data.dealer.displayName})`
      : dealerId
        ? `Dealer (#${dealerId})`
        : "Dealer",
  );

  useEffect(() => {
    if (data) {
      setLiveBotPrefs({
        dealerId: data.dealer.dealerId,
        ottoBot: data.botPreferences.ottoBot,
        outbidBot: data.botPreferences.outbidBot,
        stockNotifications: data.botPreferences.stockNotifications,
      });
      setLiveStockFilters(data.stockFilters ?? EMPTY_STOCK_FILTERS);
    }
  }, [data]);

  if (loading) return <div className="loading">Loading dealer profile...</div>;
  if (error) return <div className="error-msg">{error}</div>;
  if (!data || !liveBotPrefs) return <div className="empty-state">Dealer not found</div>;

  const tabs: Array<{ key: TabKey; label: string; count?: number }> = [
    { key: "overview", label: "Overview" },
    { key: "preferences", label: "Preferences" },
    {
      key: "feedback",
      label: "Feedback & insights",
      count: (data.insights?.insights.length ?? 0) + data.feedback.length,
    },
    { key: "outbid", label: "Outbid engagements", count: outbid.data?.total },
    { key: "tb360Bid", label: "TB360 bid engagements", count: tb360Bid.data?.total },
    { key: "conversation", label: "Conversation" },
  ];

  const profileWithLivePrefs: DealerProfile = {
    ...data,
    botPreferences: {
      ottoBot: liveBotPrefs.ottoBot,
      outbidBot: liveBotPrefs.outbidBot,
      stockNotifications: liveBotPrefs.stockNotifications,
    },
    stockFilters: liveStockFilters ?? data.stockFilters ?? EMPTY_STOCK_FILTERS,
  };

  return (
    <div className="dealer-profile-page">
      <DealerHeader profile={profileWithLivePrefs} />

      <BotPreferencesCard
        dealerId={dealerId!}
        prefs={liveBotPrefs}
        isActive={data.dealer.status === "Active"}
        onChange={setLiveBotPrefs}
      />

      <InternalCapabilitiesCard
        dealerId={dealerId!}
        initialTb360Bidding={data.internalCapabilities?.tb360Bidding ?? false}
        initialPersonalisedMessagesBlocked={data.internalCapabilities?.personalisedMessagesBlocked ?? false}
        isActive={data.dealer.status === "Active"}
      />

      <div className="dealer-profile-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`dealer-profile-tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="dealer-profile-tab-count">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "overview" && <OverviewTab profile={data} outbid={outbid.data} />}
      {activeTab === "preferences" && (
        <PreferencesTab
          profile={profileWithLivePrefs}
          dealerId={dealerId!}
          onStockFiltersSaved={setLiveStockFilters}
        />
      )}
      {activeTab === "feedback" && <FeedbackTab profile={data} />}
      {activeTab === "outbid" && (
        <OutbidEngagementTab outbid={outbid.data} loading={outbid.loading} error={outbid.error} />
      )}
      {activeTab === "tb360Bid" && (
        <Tb360BidEngagementTab tb360Bid={tb360Bid.data} loading={tb360Bid.loading} error={tb360Bid.error} />
      )}
      {activeTab === "conversation" && (
        <ConversationPreviewTab
          dealerId={dealerId!}
          conversation={conversationPreview.data}
          loading={conversationPreview.loading}
          error={conversationPreview.error}
        />
      )}
    </div>
  );
}
